import type { ApiClient } from './ApiClient';

// ENR-07 — formes alignées sur apps/api/src/routes/documentRequirements.ts et
// apps/api/src/routes/enrollmentDocuments.ts.

export interface DocumentRequirement {
  id: string;
  tenantId: string;
  name: string;
  required: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDocumentRequirementPayload {
  name: string;
  required?: boolean;
}

export function createDocumentRequirement(
  client: ApiClient,
  payload: CreateDocumentRequirementPayload,
  accessToken: string,
) {
  return client.post<{ requirement: DocumentRequirement }>('/document-requirements', payload, { accessToken });
}

export function listDocumentRequirements(client: ApiClient, accessToken: string) {
  return client.get<{ requirements: DocumentRequirement[] }>('/document-requirements', { accessToken });
}

export function updateDocumentRequirement(
  client: ApiClient,
  requirementId: string,
  payload: Partial<CreateDocumentRequirementPayload>,
  accessToken: string,
) {
  return client.patch<{ requirement: DocumentRequirement }>(`/document-requirements/${requirementId}`, payload, {
    accessToken,
  });
}

export function deleteDocumentRequirement(client: ApiClient, requirementId: string, accessToken: string) {
  return client.delete<void>(`/document-requirements/${requirementId}`, { accessToken });
}

export type EnrollmentDocumentStatus = 'PENDING' | 'VALIDATED' | 'REJECTED';

export interface EnrollmentDocumentEntry {
  id: string;
  requirementId: string;
  status: EnrollmentDocumentStatus;
  originalFilename: string;
  mimeType: string;
  fileSize: number;
  rejectionReason: string | null;
  uploadedAt: string;
  decidedAt: string | null;
}

export interface EnrollmentDocumentItem {
  requirement: { id: string; name: string; required: boolean };
  document: EnrollmentDocumentEntry | null;
}

/**
 * `file`/`requirementId` envoyés en `multipart/form-data` — `ApiClient#post`
 * détecte un corps `FormData` et laisse le navigateur poser lui-même
 * `Content-Type`/le boundary (voir ApiClient.ts).
 */
export function uploadEnrollmentDocument(
  client: ApiClient,
  enrollmentId: string,
  requirementId: string,
  file: File,
  accessToken: string,
) {
  const formData = new FormData();
  formData.append('requirementId', requirementId);
  formData.append('file', file);
  return client.post<{ document: EnrollmentDocumentEntry }>(`/enrollments/${enrollmentId}/documents`, formData, {
    accessToken,
  });
}

/** Parent — sa propre demande. */
export function listOwnEnrollmentDocuments(client: ApiClient, enrollmentId: string, accessToken: string) {
  return client.get<{ items: EnrollmentDocumentItem[] }>(`/enrollments/${enrollmentId}/documents`, { accessToken });
}

/** École — n'importe quelle demande de son propre tenant. */
export function listEnrollmentDocumentsForReview(client: ApiClient, enrollmentId: string, accessToken: string) {
  return client.get<{ items: EnrollmentDocumentItem[] }>(`/enrollments/${enrollmentId}/documents/review`, {
    accessToken,
  });
}

export interface DecideEnrollmentDocumentPayload {
  decision: 'VALIDATED' | 'REJECTED';
  reason?: string;
}

export function decideEnrollmentDocument(
  client: ApiClient,
  enrollmentId: string,
  documentId: string,
  payload: DecideEnrollmentDocumentPayload,
  accessToken: string,
) {
  return client.post<{ document: EnrollmentDocumentEntry }>(
    `/enrollments/${enrollmentId}/documents/${documentId}/decision`,
    payload,
    { accessToken },
  );
}

/**
 * Récupère le contenu binaire du document (`Blob`) — le jeton reste dans
 * l'en-tête `Authorization` (voir `ApiClient#getBlob`, jamais en paramètre
 * d'URL). L'appelant convertit ce `Blob` en URL d'objet local
 * (`URL.createObjectURL`) pour l'ouvrir/l'afficher, ex.
 * `window.open(URL.createObjectURL(blob), '_blank')`.
 */
export function fetchEnrollmentDocumentFile(
  client: ApiClient,
  enrollmentId: string,
  documentId: string,
  accessToken: string,
): Promise<Blob> {
  return client.getBlob(`/enrollments/${enrollmentId}/documents/${documentId}/file`, { accessToken });
}
